import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { X, Search, Plus, Minus, Package, Info } from 'lucide-react';
import {
    Store,
    Product,
    OrderSheet,
    Order,
    UnavailableOrderItem,
} from '../../types';
import { createOrder, updateOrder, deleteOrder, getOrder } from '../../api/orders';
import { getAdminProducts } from '../../api/products';
import { formatPhoneNumber } from '../../utils/formatters';


interface AdminOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    stores: Store[];
    products: Product[];
    orderSheets: OrderSheet[];
    initialCustomerName?: string;
    initialCustomerPhone?: string;
    initialStoreId?: string;
    initialOrderSheetId?: string;
    initialOrder?: Order;
    onRefresh?: () => void;
    onOrderMutated?: (order: Order) => void;
    onOrderDeleted?: (orderId: string) => void;
    initialUserId?: string;
}

const sanitizeImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    }
    return url;
};

const CART_KEY_SEPARATOR = '::';

const makeCartKey = (productId: string, optionId?: string) =>
    `${encodeURIComponent(productId)}${CART_KEY_SEPARATOR}${encodeURIComponent(optionId || '')}`;

const parseCartKey = (key: string) => {
    const [encodedProductId = '', encodedOptionId = ''] = key.split(CART_KEY_SEPARATOR);
    return {
        productId: decodeURIComponent(encodedProductId),
        optionId: decodeURIComponent(encodedOptionId),
    };
};

const CUSTOM_CART_PRODUCT_PREFIX = '__custom_order_item__';

const getOrderItemCartProductId = (item: Order['items'][number]) =>
    item.productId ||
    `${CUSTOM_CART_PRODUCT_PREFIX}:${item.id || `${item.name}:${item.price}`}`;

const isSameOrderItemCartProduct = (
    item: Order['items'][number],
    productId: string,
    optionId?: string,
) =>
    getOrderItemCartProductId(item) === productId &&
    (item.productOptionId || '') === (optionId || '');

const formatUnavailableItemsMessage = (items: UnavailableOrderItem[]) =>
    items
        .map((item) => {
            const availabilityLabel =
                item.availableQuantity > 0
                    ? `요청 ${item.requestedQuantity}개 / 가능 ${item.availableQuantity}개`
                    : item.reason === 'OUT_OF_STOCK'
                        ? `요청 ${item.requestedQuantity}개 / 품절`
                        : `요청 ${item.requestedQuantity}개 / 주문 불가`;
            return `- ${item.name} (${availabilityLabel})`;
        })
        .join('\n');

const getComparableOrderSignature = (order?: Order) =>
    JSON.stringify({
        id: order?.id || '',
        storeId: order?.storeId || '',
        orderSheetId: order?.orderSheetId || '',
        status: order?.status || '',
        customerName: order?.customerName || '',
        customerPhone: order?.customerPhone || '',
        totalPrice: order?.totalPrice || 0,
        request: order?.request || '',
        userId: order?.userId || '',
        items: (order?.items || []).map((item) => ({
            id: item.id || '',
            productId: item.productId,
            productOptionId: item.productOptionId || '',
            quantity: item.quantity,
            price: item.price,
        })),
    });

const getEditableStockLimit = (availableStock: number, initialQuantity: number) =>
    Math.max(0, availableStock) + Math.max(0, initialQuantity);

export const AdminOrderModal: React.FC<AdminOrderModalProps> = ({
    isOpen,
    onClose,
    stores,
    products,
    orderSheets,
    initialCustomerName = '',
    initialCustomerPhone = '',
    initialStoreId = '',
    initialOrderSheetId = '',
    initialOrder,
    onRefresh,
    onOrderMutated,
    onOrderDeleted,
    initialUserId = '',
}) => {
    const [customerName, setCustomerName] = useState(initialCustomerName);
    const [customerPhone, setCustomerPhone] = useState(initialCustomerPhone);
    const [selectedUserId, setSelectedUserId] = useState<string>(initialUserId);
    const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId);
    const [selectedOrderSheetId, setSelectedOrderSheetId] = useState<string>(initialOrderSheetId);
    const [cart, setCart] = useState<Record<string, number>>({});
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestOrder, setLatestOrder] = useState<Order | undefined>(undefined);
    const [isLoadingLatestOrder, setIsLoadingLatestOrder] = useState(false);
    const [latestOrderError, setLatestOrderError] = useState<string | null>(null);
    const [fallbackProducts, setFallbackProducts] = useState<Product[]>([]);

    const isEditMode = Boolean(initialOrder);
    const sourceOrder = latestOrder || initialOrder;
    const allProducts = products.length > 0 ? products : fallbackProducts;

    const applyOrderToForm = useCallback((order: Order) => {
        setCustomerName(order.customerName || '');
        setCustomerPhone(formatPhoneNumber(order.customerPhone) || '');
        setSelectedStoreId(order.storeId || '');
        setSelectedOrderSheetId(order.orderSheetId || initialOrderSheetId || '');
        const orderCart: Record<string, number> = {};
        order.items.forEach((item) => {
            const key = makeCartKey(getOrderItemCartProductId(item), item.productOptionId);
            orderCart[key] = item.quantity;
        });
        setCart(orderCart);
        setProductSearchTerm('');
        setSelectedUserId(order.userId || '');
    }, [initialOrderSheetId]);

    const applyCreateDefaults = useCallback(() => {
        setCustomerName(initialCustomerName);
        setCustomerPhone(formatPhoneNumber(initialCustomerPhone) || '');
        setSelectedUserId(initialUserId);
        setCart({});
        setProductSearchTerm('');

        if (initialStoreId) {
            setSelectedStoreId(initialStoreId);
        } else if (stores.length > 0) {
            setSelectedStoreId(stores[0].id);
        } else {
            setSelectedStoreId('');
        }

        if (initialOrderSheetId) {
            setSelectedOrderSheetId(initialOrderSheetId);
        } else {
            setSelectedOrderSheetId('');
        }
    }, [
        initialCustomerName,
        initialCustomerPhone,
        initialOrderSheetId,
        initialStoreId,
        initialUserId,
        stores,
    ]);

    useEffect(() => {
        if (!isOpen) {
            setFallbackProducts([]);
            return;
        }

        if (products.length > 0) {
            setFallbackProducts([]);
            return;
        }

        let isCancelled = false;

        void (async () => {
            try {
                const nextProducts = await getAdminProducts();

                if (!isCancelled) {
                    setFallbackProducts(nextProducts);
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to load admin products for modal', error);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, products.length]);

    useEffect(() => {
        if (!isOpen) {
            setCustomerName('');
            setCustomerPhone('');
            setSelectedStoreId('');
            setSelectedOrderSheetId('');
            setCart({});
            setProductSearchTerm('');
            setSelectedUserId('');
            setLatestOrder(undefined);
            setLatestOrderError(null);
            setIsLoadingLatestOrder(false);
            return;
        }

        if (!initialOrder) {
            setLatestOrder(undefined);
            setLatestOrderError(null);
            setIsLoadingLatestOrder(false);
            applyCreateDefaults();
            return;
        }

        let isCancelled = false;

        applyOrderToForm(initialOrder);
        setLatestOrder(initialOrder);
        setLatestOrderError(null);
        setIsLoadingLatestOrder(true);

        void (async () => {
            try {
                const refreshedOrder = await getOrder(initialOrder.id);

                if (isCancelled) {
                    return;
                }

                applyOrderToForm(refreshedOrder);
                setLatestOrder(refreshedOrder);

                if (
                    getComparableOrderSignature(refreshedOrder)
                        !== getComparableOrderSignature(initialOrder)
                ) {
                    onOrderMutated?.(refreshedOrder);
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                console.error('Failed to fetch latest admin order', error);
                setLatestOrderError('최신 주문 정보를 불러오지 못해 현재 화면 데이터로 편집합니다.');
                setLatestOrder(initialOrder);
            } finally {
                if (!isCancelled) {
                    setIsLoadingLatestOrder(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [
        applyCreateDefaults,
        applyOrderToForm,
        initialOrder,
        isOpen,
        onOrderMutated,
    ]);

    const storeOrderSheetMapping = useMemo(() => {
        const mapping = new Map<string, OrderSheet>();
        for (const store of stores) {
            const sheetsForStore = orderSheets.filter(s => s.storeId === store.id);
            if (sheetsForStore.length > 0) {
                const latestSheet = sheetsForStore.reduce((latest, current) =>
                    new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
                );
                mapping.set(store.id, latestSheet);
            }
        }
        return mapping;
    }, [stores, orderSheets]);

    const activeOrderSheet = useMemo(() => {
        if (!selectedStoreId) return null;

        // If we have a specifically selected sheet for this store, use it
        if (selectedOrderSheetId) {
            const sheet = orderSheets.find(s => s.id === selectedOrderSheetId);
            if (sheet && sheet.storeId === selectedStoreId) {
                return sheet;
            }
        }

        // Fallback to latest
        return storeOrderSheetMapping.get(selectedStoreId) || null;
    }, [selectedStoreId, selectedOrderSheetId, storeOrderSheetMapping, orderSheets]);

    const availableProducts = useMemo(() => {
        // If there's an active order sheet with products, use those. Otherwise show all products (fallback for admins)
        let baseProducts = [];
        const cartProductIds = new Set(Object.keys(cart).map(key => parseCartKey(key).productId));
        if (activeOrderSheet?.products && activeOrderSheet.products.length > 0) {
            const sheetProductIds = activeOrderSheet.products.map(p => p.id);
            baseProducts = allProducts.filter(p => sheetProductIds.includes(p.id) || cartProductIds.has(p.id));
        } else {
            baseProducts = allProducts;
        }

        return baseProducts.filter(p => {
            const isMatch = p.name.toLowerCase().includes(productSearchTerm.toLowerCase());
            return isMatch;
        }).sort((a, b) => {
            const getIsAvailable = (p: Product) => {
                const stock = p.availableStock ?? p.totalStock ?? 0;
                return stock > 0 && !p.isClosed;
            };

            const aAvailable = getIsAvailable(a);
            const bAvailable = getIsAvailable(b);

            if (aAvailable !== bAvailable) {
                return aAvailable ? -1 : 1;
            }

            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (dateA !== dateB) return dateB - dateA;

            return a.name.localeCompare(b.name);
        });
    }, [activeOrderSheet, allProducts, productSearchTerm, cart]);

    const handleSetQuantity = (key: string, quantity: number) => {
        setCart(prev => {
            const { productId, optionId } = parseCartKey(key);
            const product = allProducts.find(p => p.id === productId);
            const initialItem = sourceOrder?.items.find((i) =>
                isSameOrderItemCartProduct(i, productId, optionId),
            );
            if (!product) {
                const newQty = Math.max(0, quantity);
                if (newQty === 0 && !initialItem) {
                    const { [key]: _, ...rest } = prev;
                    return rest;
                }
                return { ...prev, [key]: newQty };
            }

            const initialQty = initialItem?.quantity || 0;
            
            let available = product.availableStock ?? product.totalStock ?? Number.MAX_SAFE_INTEGER;
            if (optionId) {
                const opt = product.options?.find(o => o.id === optionId);
                if (opt) available = opt.availableStock ?? opt.stock ?? Number.MAX_SAFE_INTEGER;
            }

            const maxAllowed = getEditableStockLimit(available, initialQty);
            const newQty = Math.max(0, Math.min(maxAllowed, quantity));

            if (newQty === 0 && !initialItem) {
                const { [key]: _, ...rest } = prev;
                return rest;
            }

            return { ...prev, [key]: newQty };
        });
    };

    const handleRemoveProduct = (key: string) => {
        setCart(prev => {
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    };

    const totals = useMemo(() => {
        return Object.entries(cart).reduce((acc, [key, qty]) => {
            const { productId, optionId } = parseCartKey(key);
            const product = allProducts.find(p => p.id === productId);
            const existingItem = sourceOrder?.items.find((i) =>
                isSameOrderItemCartProduct(i, productId, optionId),
            );
            if (product) {
                acc.count += (qty as number);
                let price = product.price;
                if (optionId) {
                    const opt = product.options?.find(o => o.id === optionId);
                    if (opt && opt.price !== undefined) price = opt.price;
                }
                acc.price += price * (qty as number);
            } else if (existingItem) {
                acc.count += (qty as number);
                acc.price += (existingItem.price || 0) * (qty as number);
            }
            return acc;
        }, { count: 0, price: 0 });
    }, [allProducts, cart, sourceOrder]);

    const currentCartEntries = useMemo(
        () => Object.entries(cart).filter(([_, qty]) => (qty as number) > 0),
        [cart]
    );

    const activeCartKeySet = useMemo(
        () => new Set(currentCartEntries.map(([key]) => key)),
        [currentCartEntries]
    );

    const additionalProducts = useMemo(
        () =>
            availableProducts.filter((product) => {
                const sortedOptions = [...(product.options || [])].sort(
                    (a, b) => (a.position ?? 0) - (b.position ?? 0)
                );

                if (sortedOptions.length > 0) {
                    return sortedOptions.some(
                        (option) => !activeCartKeySet.has(makeCartKey(product.id, option.id))
                    );
                }

                return !activeCartKeySet.has(makeCartKey(product.id));
            }),
        [availableProducts, activeCartKeySet]
    );

    const handleSubmit = async () => {
        if (!customerName.trim() || !customerPhone.trim()) {
            alert('고객명과 전화번호를 입력해주세요.');
            return;
        }
        if (!selectedStoreId) {
            alert('배송지를 선택해주세요.');
            return;
        }
        if (totals.count === 0) {
            alert('상품을 1개 이상 선택해주세요.');
            return;
        }

        const currentStore = stores.find(s => s.id === selectedStoreId);
        if (!currentStore) return;

        try {
            setIsSubmitting(true);
            const items = Object.entries(cart).filter(([_, qty]) => (qty as number) > 0).map(([key, qty]) => {
                const { productId, optionId } = parseCartKey(key);
                const p = allProducts.find(prod => prod.id === productId);
                const opt = p?.options?.find(o => o.id === optionId);

                if (!p) {
                    // Fallback for cases where product was deleted but was previously in the cart/order
                    const existingItem = sourceOrder?.items.find((i) =>
                        isSameOrderItemCartProduct(i, productId, optionId),
                    );
                    return {
                        productId: existingItem?.productId,
                        productOptionId: optionId || undefined,
                        name: existingItem?.name || 'Deleted Product',
                        optionName: existingItem?.optionName,
                        price: existingItem?.price || 0,
                        quantity: qty as number,
                        image: existingItem?.image || ''
                    };
                }
                
                return { 
                    productId: productId, 
                    productOptionId: optionId || undefined,
                    name: p.name, 
                    optionName: opt?.name,
                    price: opt?.price !== undefined ? opt.price : p.price, 
                    quantity: qty as number, 
                    image: p.image || '' 
                };
            });

            const orderData = {
                storeId: currentStore.id,
                storeName: currentStore.name,
                ...(activeOrderSheet?.id && { orderSheetId: activeOrderSheet.id }),
                items,
                totalPrice: totals.price,
                customerName: customerName.trim(),
                customerPhone: customerPhone.trim(),
                request: isEditMode
                    ? sourceOrder?.request || undefined
                    : '판매자 대신 주문',
                isForced: true, // Bypass sales period checks for admin orders
                modifierRole: 'SELLER',
                userId: selectedUserId || undefined,
            };

            if (isEditMode && sourceOrder) {
                const updatedOrder = await updateOrder(sourceOrder.id, orderData);
                onOrderMutated?.(updatedOrder);
                alert('주문 수정이 완료되었습니다.');
            } else {
                const { order, unavailableItems, resultType } = await createOrder(orderData);
                if (!order) {
                    alert(
                        `주문 가능한 상품이 없어 주문이 생성되지 않았습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
                    );
                    return;
                }

                if (resultType === 'UNCHANGED_WITH_UNAVAILABLE') {
                    alert(
                        `재고 부족 또는 주문 불가 상품은 반영되지 않아 기존 주문 상품이 그대로 유지되었습니다.\n\n반영되지 않은 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
                    );
                } else if (unavailableItems.length > 0) {
                    alert(
                        `일부 상품은 품절 또는 주문 불가로 제외되고 나머지만 주문되었습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
                    );
                } else {
                    alert('주문이 완료되었습니다.');
                }

                const latestCreatedOrder = await getOrder(order.id);
                onOrderMutated?.(latestCreatedOrder);
            }
            if (onRefresh) onRefresh();
            onClose();
        } catch (error: any) {
            console.error('Failed to create admin order', error);
            let errorMessage = isEditMode ? '주문 수정에 실패했습니다.' : '주문 생성에 실패했습니다.';

            // Try to extract specific error message from backend
            if (error?.response?.data?.message) {
                const msg = error.response.data.message;
                if (msg === 'Store is closed' || msg.includes('주문을 마감')) {
                    errorMessage = '영업이 마감된 주문지입니다.';
                } else if (msg.includes('Stock insufficient')) {
                    errorMessage = `재고가 부족합니다: ${msg.split('Stock insufficient for ')[1] || ''}`;
                } else if (msg.includes('Ordering is closed') || msg.includes('주문 기간이 종료')) {
                    errorMessage = '주문 기간이 종료된 주문지입니다.';
                } else {
                    errorMessage = msg;
                }
            }

            alert(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!sourceOrder) return;
        if (!confirm('정말 이 주문을 영구 삭제하시겠습니까?\n주문 이력도 함께 삭제됩니다. 일반 취소는 주문 상태 변경을 사용해주세요.')) return;

        try {
            setIsSubmitting(true);
            await deleteOrder(sourceOrder.id);
            onOrderDeleted?.(sourceOrder.id);
            alert('주문이 삭제되었습니다.');
            if (onRefresh) onRefresh();
            onClose();
        } catch (error) {
            console.error('Failed to delete admin order', error);
            alert('주문 삭제에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[8000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white rounded-[32px] overflow-hidden flex flex-col max-h-[90vh] shadow-2xl animate-in zoom-in duration-200">

                {/* Header */}
                <div className="p-6 border-b shrink-0 flex items-center justify-between bg-gray-50">
                    <div>
                        <h2 className="text-xl font-black text-gray-900">{isEditMode ? '주문서 수정' : '대신 주문하기'}</h2>
                        <p className="text-[11px] font-bold text-gray-400 mt-1">{isEditMode ? '기존 주문 내역을 수정합니다.' : '사장님이 직접 고객의 주문을 생성합니다.'}</p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-white rounded-full text-gray-400 border border-gray-100 shadow-sm hover:text-gray-900 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Form Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {isEditMode && (isLoadingLatestOrder || latestOrderError) && (
                        <div
                            className={`rounded-2xl border px-4 py-3 text-[11px] font-bold ${
                                latestOrderError
                                    ? 'border-orange-100 bg-orange-50 text-orange-600'
                                    : 'border-emerald-100 bg-emerald-50 text-emerald-700'
                            }`}
                        >
                            {latestOrderError || '최신 주문 정보를 불러오는 중입니다.'}
                        </div>
                    )}

                    <div className="space-y-4">
                        <h3 className="text-sm font-black text-gray-900 border-l-4 border-emerald-500 pl-2">고객 정보</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-gray-500">고객명</label>
                                <input
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    className="w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold placeholder-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                                    placeholder="홍길동"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-gray-500">연락처</label>
                                <input
                                    value={formatPhoneNumber(customerPhone)}
                                    onChange={(e) => setCustomerPhone(e.target.value.replace(/[^0-9]/g, ''))}
                                    className="w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold placeholder-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                                    placeholder="010-1234-5678"
                                    maxLength={13}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-black text-gray-900 border-l-4 border-emerald-500 pl-2">주문지 선택</h3>
                        <select
                            value={selectedStoreId}
                            onChange={(e) => {
                                setSelectedStoreId(e.target.value);
                                setSelectedOrderSheetId(''); // Clear specific selection when store changes
                            }}
                            disabled={isEditMode}
                            className={`w-full h-11 bg-gray-50 border border-gray-100 rounded-xl px-3 text-sm font-bold text-gray-900 outline-none ${isEditMode ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''}`}
                        >
                            <option value="" disabled>주문지 선택</option>
                            {stores.map(s => {
                                // Priority: specifically selected sheet > latest sheet
                                let sheet = orderSheets.find(os => os.id === selectedOrderSheetId && os.storeId === s.id);
                                if (!sheet) {
                                    sheet = storeOrderSheetMapping.get(s.id);
                                }

                                const dateStr = sheet ? new Date(sheet.deliveryDate).toLocaleDateString() : '';
                                const label = sheet ? `${s.name} - ${sheet.label || `${dateStr} 배송`}` : `${s.name} (주문서 없음)`;
                                return <option key={s.id} value={s.id}>{label}</option>;
                            })}
                        </select>
                        {!activeOrderSheet && selectedStoreId && (
                            <div className="bg-orange-50 text-orange-600 border border-orange-100 p-3 rounded-xl flex items-start gap-2 text-[11px] font-bold">
                                <Info size={14} className="shrink-0 mt-0.5" />
                                최신 주문서가 없습니다. 전체 상품 목록 중 판매 중인 상품(및 현재 주문된 상품)만 표시됩니다.
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-end">
                            <h3 className="text-sm font-black text-gray-900 border-l-4 border-emerald-500 pl-2">상품 선택</h3>
                        </div>

                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                placeholder="상품명 검색..."
                                value={productSearchTerm}
                                onChange={(e) => setProductSearchTerm(e.target.value)}
                                className="w-full h-10 bg-gray-50 border border-gray-100 rounded-xl pl-9 pr-3 text-xs font-bold outline-none placeholder-gray-400"
                            />
                        </div>

                        <div className="space-y-6">
                            {/* Ordered Products Section */}
                            {currentCartEntries.length > 0 && (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 px-1">
                                        <div className="w-1 h-4 bg-emerald-500 rounded-full" />
                                        <h3 className="text-[13px] font-black text-gray-900">현재 주문한 상품</h3>
                                        <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                                            {currentCartEntries.length}개 항목
                                        </span>
                                    </div>
                                    <div className="space-y-3">
                                        {currentCartEntries.map(([key, qty]) => {
                                            const { productId, optionId } = parseCartKey(key);
                                            const p = allProducts.find(prod => prod.id === productId);
                                            const opt = p?.options?.find(o => o.id === optionId);
                                            const existingItem = sourceOrder?.items.find((i) =>
                                                isSameOrderItemCartProduct(i, productId, optionId),
                                            );
                                            const displayName = p?.name || existingItem?.name || '삭제된 상품';
                                            const displayOptionName = opt?.name || existingItem?.optionName;
                                            const displayImage = sanitizeImageUrl(p?.image || existingItem?.image);
                                            const displayPrice = opt?.price !== undefined
                                                ? opt.price
                                                : p?.price ?? existingItem?.price ?? 0;
                                            const availableStock = opt
                                                ? (opt.availableStock ?? opt.stock)
                                                : (p?.availableStock ?? p?.totalStock);
                                            const initialQuantity = sourceOrder?.items.find((i) =>
                                                isSameOrderItemCartProduct(i, productId, optionId),
                                            )?.quantity || 0;

                                            return (
                                                <div key={key} className="flex gap-3 p-3 border border-emerald-100 bg-white rounded-2xl shadow-md ring-1 ring-emerald-500/10 relative group transition-all">
                                                    {displayImage ? (
                                                        <img src={displayImage} alt={displayName} className="w-16 h-16 object-cover rounded-xl bg-gray-50 border shrink-0" />
                                                    ) : (
                                                        <div className="w-16 h-16 rounded-xl bg-gray-50 border shrink-0 flex items-center justify-center text-gray-300">
                                                            <Package size={20} />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                                                        <div>
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <h4 className="text-[13px] font-black line-clamp-2 leading-tight text-gray-900">{displayName}</h4>
                                                                    {displayOptionName && <p className="text-[10px] font-bold text-emerald-600 mt-0.5">옵션: {displayOptionName}</p>}
                                                                </div>
                                                                <button
                                                                    onClick={() => handleRemoveProduct(key)}
                                                                    className="p-1 -mr-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                                                                    title="삭제"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </div>
                                                            {p?.isClosed && (
                                                                <div className="mt-1">
                                                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[8px] font-black rounded-md">판매중지</span>
                                                                </div>
                                                            )}
                                                            {!p && (
                                                                <div className="mt-1">
                                                                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[8px] font-black rounded-md">현재 상품 정보 없음</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex items-center justify-between mt-1">
                                                            <div className="space-y-0.5">
                                                                <p className="text-[14px] font-black text-emerald-600">{displayPrice.toLocaleString()}원</p>
                                                                {p ? (
                                                                    <p className="text-[10px] font-bold text-gray-400">
                                                                        재고: <span>{availableStock ?? 0}개</span>
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-[10px] font-bold text-gray-400">현재 재고 정보 없음</p>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-1">
                                                                <button
                                                                    onClick={() => handleSetQuantity(key, (qty as number) - 1)}
                                                                    className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                                    disabled={!p || (qty as number) <= 0}
                                                                >
                                                                    <Minus size={14} />
                                                                </button>
                                                                <span className="min-w-[20px] text-center text-[13px] font-black text-gray-900">{qty}</span>
                                                                <button
                                                                    onClick={() => handleSetQuantity(key, (qty as number) + 1)}
                                                                    disabled={!p || (qty as number) >= getEditableStockLimit(availableStock ?? 0, initialQuantity)}
                                                                    className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                                >
                                                                    <Plus size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Rest of the Products Section */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 px-1">
                                    <div className="w-1 h-4 bg-gray-300 rounded-full" />
                                    <h3 className="text-[13px] font-black text-gray-500">추가 가능한 상품</h3>
                                </div>
                                <div className="space-y-3">
                                    {additionalProducts.map((p) => {
                                        const sortedOptions = [...(p.options || [])].sort(
                                            (a, b) => (a.position ?? 0) - (b.position ?? 0)
                                        );

                                        if (sortedOptions.length > 0) {
                                            const visibleOptions = sortedOptions.filter(
                                                (option) => !activeCartKeySet.has(makeCartKey(p.id, option.id))
                                            );
                                            const minPrice = Math.min(
                                                ...sortedOptions.map((option) => option.price ?? p.price)
                                            );
                                            const totalStock = sortedOptions.reduce(
                                                (sum, option) => sum + (option.availableStock ?? option.stock ?? 0),
                                                0
                                            );

                                            return (
                                                <div key={p.id} className="p-3 border border-gray-100 bg-gray-50/50 rounded-2xl space-y-3">
                                                    <div className="flex gap-3">
                                                        <img
                                                            src={sanitizeImageUrl(p.image)}
                                                            alt={p.name}
                                                            className="w-16 h-16 object-cover rounded-xl bg-gray-50 border shrink-0 opacity-80"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <h4 className="text-[13px] font-black line-clamp-2 leading-tight text-gray-700">
                                                                        {p.name}
                                                                    </h4>
                                                                    <p className="text-[10px] font-bold text-gray-400 mt-1">
                                                                        {minPrice.toLocaleString()}원부터 · 재고 {totalStock}개
                                                                    </p>
                                                                </div>
                                                                {sourceOrder?.items.some(i => i.productId === p.id) && (
                                                                    <span className="shrink-0 text-[9px] font-black bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">
                                                                        취소예정
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {p.isClosed && (
                                                                <div className="mt-1">
                                                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[8px] font-black rounded-md">
                                                                        판매중지
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        {visibleOptions.map((option) => {
                                                            const key = makeCartKey(p.id, option.id);
                                                            const qty = cart[key] || 0;
                                                            const optionStock = option.availableStock ?? option.stock ?? 0;
                                                            const initialQty =
                                                                sourceOrder?.items.find(
                                                                    i => i.productId === p.id && i.productOptionId === option.id
                                                                )?.quantity || 0;
                                                            const maxAllowed = getEditableStockLimit(optionStock, initialQty);

                                                            return (
                                                                <div
                                                                    key={option.id}
                                                                    className="flex items-center justify-between gap-3 rounded-xl bg-white border border-gray-100 px-3 py-3"
                                                                >
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex items-start justify-between gap-2">
                                                                            <div className="min-w-0">
                                                                                <p className="text-[12px] font-black text-gray-800 truncate">
                                                                                    {option.name}
                                                                                </p>
                                                                                <p className="text-[10px] font-bold text-gray-400 mt-1">
                                                                                    {(option.price ?? p.price).toLocaleString()}원
                                                                                    {' '}· 재고 {optionStock}개
                                                                                </p>
                                                                            </div>
                                                                            {sourceOrder?.items.some(
                                                                                i => i.productId === p.id && i.productOptionId === option.id
                                                                            ) && (
                                                                                <span className="shrink-0 text-[9px] font-black bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">
                                                                                    취소예정
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-1 shrink-0">
                                                                        <button
                                                                            onClick={() => handleSetQuantity(key, qty - 1)}
                                                                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                                            disabled={qty <= 0}
                                                                        >
                                                                            <Minus size={14} />
                                                                        </button>
                                                                        <span className="min-w-[20px] text-center text-[13px] font-black text-gray-500">
                                                                            {qty}
                                                                        </span>
                                                                        <button
                                                                            onClick={() => handleSetQuantity(key, qty + 1)}
                                                                            disabled={qty >= maxAllowed}
                                                                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                                        >
                                                                            <Plus size={14} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const qty = cart[makeCartKey(p.id)] || 0;
                                        const key = makeCartKey(p.id);

                                        return (
                                            <div key={p.id} className="flex gap-3 p-3 border border-gray-100 bg-gray-50/50 rounded-2xl relative group transition-all">
                                                <img src={sanitizeImageUrl(p.image)} alt={p.name} className="w-16 h-16 object-cover rounded-xl bg-gray-50 border shrink-0 opacity-80" />
                                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                                    <div>
                                                        <div className="flex items-start justify-between gap-2">
                                                            <h4 className="text-[13px] font-black line-clamp-2 leading-tight flex-1 text-gray-600">{p.name}</h4>
                                                            {sourceOrder?.items.some(i => i.productId === p.id && !i.productOptionId) && (
                                                                <span className="shrink-0 text-[9px] font-black bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">취소예정</span>
                                                            )}
                                                        </div>
                                                        {p.isClosed && (
                                                            <div className="mt-1">
                                                                <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[8px] font-black rounded-md">판매중지</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center justify-between mt-1">
                                                        <div className="space-y-0.5">
                                                            <p className="text-[14px] font-black text-gray-500">{p.price.toLocaleString()}원</p>
                                                            <p className="text-[10px] font-bold text-gray-400">
                                                                재고: <span className={p.availableStock === 0 ? 'text-red-400' : 'text-gray-500'}>{p.availableStock ?? p.totalStock ?? 0}개</span>
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-1">
                                                            <button
                                                                onClick={() => handleSetQuantity(key, qty - 1)}
                                                                className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                                disabled={qty <= 0}
                                                            >
                                                                <Minus size={14} />
                                                            </button>
                                                            <span className="min-w-[20px] text-center text-[13px] font-black text-gray-500">{qty}</span>
                                                            <button
                                                                onClick={() => handleSetQuantity(key, qty + 1)}
                                                                disabled={qty >= getEditableStockLimit(p.availableStock ?? p.totalStock ?? 0, sourceOrder?.items.find(i => i.productId === p.id && !i.productOptionId)?.quantity || 0)}
                                                                className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-30"
                                                            >
                                                                <Plus size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {additionalProducts.length === 0 && (
                                        <div className="py-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                            <Package size={24} className="mx-auto text-gray-300 mb-2" />
                                            <p className="text-[11px] font-bold text-gray-400">추가 가능한 상품이 없습니다.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-white shrink-0 flex gap-3">
                    {isEditMode && (
                        <button
                            onClick={handleDelete}
                            disabled={isSubmitting || isLoadingLatestOrder}
                            className="flex-1 h-14 bg-red-50 text-red-500 border border-red-100 rounded-2xl font-black text-sm hover:bg-red-100 transition-colors"
                        >
                            삭제하기
                        </button>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || isLoadingLatestOrder || totals.count === 0}
                        className={`${isEditMode ? 'flex-[2]' : 'w-full'} h-14 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white rounded-2xl flex items-center justify-between px-6 transition-colors shadow-lg shadow-emerald-500/20`}
                    >
                        <span className="text-sm font-black flex items-center gap-2">
                            <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-xs">{totals.count}</span>
                            {isEditMode ? '수정 저장' : '주문 접수'}
                        </span>
                        <span className="text-lg font-black">{totals.price.toLocaleString()}원</span>
                    </button>
                </div>

            </div>
        </div>
    );
};
